/**
 * Pure geometry + formatting helpers for the unified select / pause
 * mode (select-to-chat, `docs/design/preview-select-to-chat-prd.md`).
 *
 * Everything here is pure and unit-tested
 * (`__tests__/select-area.test.ts`). The DOM/pointer halves live in the
 * injected inspector (per iframe) and the renderer hook
 * (`usePreviewInspector`), which stay thin.
 *
 * Coordinate spaces (mixing them is THE bug class this module guards):
 *   - INSPECTOR space — CSS px in a preview iframe's own viewport (what
 *     `getBoundingClientRect()` returns inside that iframe). Every
 *     selection rect crosses the bridge in this space, tagged with the
 *     iframe's `viewport` size.
 *   - WINDOW space — Foundry-window CSS px, what `preview:capture-area`
 *     wants. `scalePaneRect` maps inspector→display-box px; the caller
 *     adds the iframe's client offset.
 *   - SURFACE space — what the agent reads: the rect within the
 *     screen's own viewport (so `surfaceSize` == the inspector
 *     viewport). No remap needed — the inspector rect IS surface space.
 */

/** Axis-aligned rect. The coordinate space is the caller's contract. */
export interface SelectionRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Clamp a rect to `[0,0]..[boundsWidth,boundsHeight]` (same space as
 *  the bounds). Returns null when nothing remains. */
export function clampRectToBounds(
  rect: SelectionRect,
  boundsWidth: number,
  boundsHeight: number,
): SelectionRect | null {
  const left = Math.max(0, rect.x);
  const top = Math.max(0, rect.y);
  const right = Math.min(boundsWidth, rect.x + rect.width);
  const bottom = Math.min(boundsHeight, rect.y + rect.height);
  if (right - left <= 0 || bottom - top <= 0) return null;
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/**
 * Map a rect from one coordinate space into another by uniform
 * per-axis scaling, rounding outward (floor origin / ceil far edge) so
 * the mapped rect never loses its last pixel row/column, then clamping
 * to the target bounds. Used inspector→display-box px for capture.
 */
export function scalePaneRect(
  rect: SelectionRect,
  fromWidth: number,
  fromHeight: number,
  targetWidth: number,
  targetHeight: number,
): SelectionRect | null {
  if (fromWidth <= 0 || fromHeight <= 0 || targetWidth <= 0 || targetHeight <= 0) return null;
  const sx = targetWidth / fromWidth;
  const sy = targetHeight / fromHeight;
  const left = Math.floor(rect.x * sx);
  const top = Math.floor(rect.y * sy);
  const right = Math.ceil((rect.x + rect.width) * sx);
  const bottom = Math.ceil((rect.y + rect.height) * sy);
  return clampRectToBounds(
    { x: left, y: top, width: right - left, height: bottom - top },
    targetWidth,
    targetHeight,
  );
}

/**
 * A DOM-element descriptor carried in an element selection. Mirrors
 * `ElementDescriptor` from `inspect-protocol.ts` but re-declared to
 * keep this pure module dependency-free. The agent localises via
 * `text` (its main grep key) + the crop — no scene-node id, since
 * created-game code is untouched.
 */
export interface SelectionElementInfo {
  tag: string;
  text: string;
  classes: string[];
  role?: string;
  ariaLabel?: string;
  src?: string;
}

/**
 * The structured selection attached to the chat message. `surface` is
 * which screen it came from (`tv` or `controller:N`); `rect` +
 * `surfaceSize` are in that screen's own viewport coordinates.
 */
export interface PreviewSelectionContext {
  kind: 'area' | 'element';
  surface: string;
  rect: SelectionRect;
  surfaceSize: { width: number; height: number };
  /** Present only for element selections. */
  element?: SelectionElementInfo;
  /** Visible text of the whole screen the selection came from, so the
   *  agent can identify which scene/state it is (Option A). */
  screenText?: string;
}

/**
 * Render the fenced context block prepended to the user's message at
 * send time. For element selections the agent greps the visible
 * `text`; for both, `rect`/`surfaceSize` say WHERE on which screen.
 */
export function formatSelectionContext(context: PreviewSelectionContext): string {
  const { rect, surfaceSize } = context;
  const lines = [
    '```crucible-selection',
    `kind: ${context.kind}`,
    `surface: ${context.surface}`,
    `rect: { x: ${rect.x}, y: ${rect.y}, w: ${rect.width}, h: ${rect.height} }`,
    `surfaceSize: ${surfaceSize.width}x${surfaceSize.height}`,
  ];
  if (context.kind === 'element' && context.element) {
    const el = context.element;
    lines.push(`element: <${el.tag}>`);
    if (el.text) lines.push(`text: ${JSON.stringify(el.text)}`);
    if (el.classes.length > 0) lines.push(`classes: ${el.classes.join(' ')}`);
    if (el.role) lines.push(`role: ${el.role}`);
    if (el.ariaLabel) lines.push(`ariaLabel: ${JSON.stringify(el.ariaLabel)}`);
    if (el.src) lines.push(`src: ${el.src}`);
    lines.push(
      'note: no scene-node id (the game DOM is not instrumented) — locate the source by ' +
        'grepping the visible text/label, then confirm against the attached crop.',
    );
  } else {
    lines.push('image: the attached screenshot crop shows exactly this region');
  }
  if (context.screenText) {
    lines.push(
      'screenText: visible text of the whole screen — use it to identify which scene/state ' +
        'this is, then grep for it to find the matching manifest:',
    );
    lines.push(`screenText: ${JSON.stringify(context.screenText)}`);
  }
  lines.push('```');
  return lines.join('\n');
}
